import type { BetslipTicketType } from '../../@types';
import { analyzeBetslip, isWaysBetslip } from '../../helpers';
import { MarketChangeStatus, MatchChangeStatus, OutcomeChangeStatus } from '../../helpers/betslipMessageParser';
import type { BetslipEvent } from '../../types/betslip';
import { isBetBuilderBet } from '../../utils/betBuilder';
import type { BetslipRecord } from './persistence';

// Error/info messages are stored as i18n keys; consumers translate at render time.
export const BETSLIP_ERROR_KEYS = {
  availabilityChanged: 'selectionAvailabilityChanged',
  priceChanged: 'selectionPriceChanged',
} as const;

export type BetslipDerivedState = {
  selectedOutcomeKeys: ReadonlySet<string>;
  betCount: number;
  betslipTicketType: BetslipTicketType;
  uniqueEventCount: number;
  currentBankerCount: number;
  isMaxBankerCountReached: boolean;
  isWaysTicket: boolean;
  hasBetBuilderSelection: boolean;
  errorMessageKeys: string[];
};

const checkIsDeactivated = (bet: BetslipEvent) =>
  bet.outcomeChange === OutcomeChangeStatus.deactivated ||
  bet.marketChange === MarketChangeStatus.deactivated ||
  bet.matchChange === MatchChangeStatus.deactivated;

const deriveErrorMessageKeys = (bets: BetslipEvent[]): string[] => {
  const keys = new Set<string>();

  bets.forEach((bet) => {
    if (checkIsDeactivated(bet)) {
      keys.add(BETSLIP_ERROR_KEYS.availabilityChanged);
    } else if (bet.oddsChange) {
      keys.add(BETSLIP_ERROR_KEYS.priceChanged);
    }
  });

  return Array.from(keys);
};

const haveSameItems = (previous: string[] | undefined, next: string[]) =>
  !!previous && previous.length === next.length && previous.every((value, index) => value === next[index]);

/**
 * Recomputes every value derived from `bets` in a single pass. Called once per store commit so
 * derived state always changes in the same update as `bets` (no follow-up effect renders).
 *
 * `selectedOutcomeKeys` flattens `isOutcomeInBetslip` semantics (utils/betBuilder.ts): a bet's own
 * `outcomeId` plus every bet-builder child outcome in `betBuilder.selectedOdds`.
 */
export const deriveFromBets = (
  bets: BetslipRecord,
  previous?: Pick<BetslipDerivedState, 'betCount' | 'isWaysTicket' | 'errorMessageKeys'>
): BetslipDerivedState => {
  const betList = Object.values(bets);

  const selectedOutcomeKeys = new Set<string>();
  betList.forEach((bet) => {
    selectedOutcomeKeys.add(bet.outcomeId);
    bet.betBuilder?.selectedOdds.forEach((outcomeId) => selectedOutcomeKeys.add(outcomeId));
  });

  const { betCount, betslipTicketType } = analyzeBetslip(betList);

  const currentBankerCount = betList.filter((bet) => bet.banker).length;
  const maxBankerCount = betCount - 2;

  // Ways-ness only depends on which selections are in the slip, not on odds/status updates —
  // recompute only when the selection count changes (mirrors the original betCount-keyed useMemo).
  const isWaysTicket =
    previous && previous.betCount === betCount
      ? previous.isWaysTicket
      : isWaysBetslip(betList.filter((bet) => !isBetBuilderBet(bet)));

  const errorMessageKeys = deriveErrorMessageKeys(betList);

  return {
    selectedOutcomeKeys,
    betCount,
    betslipTicketType,
    uniqueEventCount: new Set(betList.map((bet) => bet.eventId)).size,
    currentBankerCount,
    isMaxBankerCountReached: betCount > 2 && currentBankerCount >= maxBankerCount,
    isWaysTicket,
    hasBetBuilderSelection: betList.some(isBetBuilderBet),
    errorMessageKeys: haveSameItems(previous?.errorMessageKeys, errorMessageKeys)
      ? (previous as BetslipDerivedState).errorMessageKeys
      : errorMessageKeys,
  };
};
